/**
 * UserInfo page
 */

// React
import React, { FC } from 'react';

// Material
import { Table, TableBody, TableCell, TableRow, Box, IconButton } from '@mui/material';

// Components
import SelectInput from 'components/SelectInput';
import SearchInput from 'components/SearchInput';
import TableHeader from 'components/TableHeader';
import TotalRecords from 'components/TotalRecords';
import ProcessButton from 'components/ProcessButton';
import PaginationComponent from 'components/Pagination';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';

// Data mock
import { defaultParamsGetRegisterUser, headerRegisterUser, menuItems } from 'initDataPages';

// Images
import { setting_icon, remove_icon } from 'assets/icons';

// Common styles
import {
  BoxNoDataList,
  PageContainer,
  CustomTableHead,
  RecordsContainer,
  CustomTableContainer,
  WrapperSearchAndButton,
} from 'styles';

// Utils
import { DataRowRegisterUser } from 'utils/types';
import { NOTIFICATION_MESSAGES, SYSTEM_MENU } from 'utils/constants';

// Hooks
import useGetListData from 'hooks/useGetListData';

// Services
import UserService from 'services/UserService';

// Component
const UserInfo: FC = () => {
  const {
    state,
    perPage,
    dataList,
    isLoading,
    totalPages,
    currentPage,
    totalRecords,
    paramsGetList,
    scrollContainerRef,

    getIndex,
    isSortable,
    handleClose,
    handleExport,
    handleRemove,
    isIconVisible,
    handleChangePage,
    handleRequestSort,
    navigateEditSystem,
    handleConfirmDelete,
    getIconUrlWithStatus,
    isDisabledEditSystem,
  } = useGetListData<DataRowRegisterUser>(
    (paramsGetList) => UserService.getUsers(paramsGetList),
    (paramsGetList) => UserService.exportUsers(paramsGetList),
    (state) => UserService.deleteUser({ id: state?.id }),
    defaultParamsGetRegisterUser,
    SYSTEM_MENU.REGISTRANT_INFO,
    NOTIFICATION_MESSAGES.ERROR.DOWNLOAD
  );

  return (
    <PageContainer>
      <ConfirmDeleteModal
        open={state.openRemove ?? false}
        messages="この項目を削除して宜しいですか？"
        handleClose={handleClose}
        handleConfirmDelete={handleConfirmDelete}
      />

      <SelectInput menuItems={menuItems} />

      <RecordsContainer>
        <TotalRecords
          recordsPerPage={perPage}
          currentPage={currentPage}
          totalRecords={totalRecords}
        />
        <WrapperSearchAndButton>
          <SearchInput />
          <ProcessButton disabled={!totalRecords} handleDownload={handleExport} />
        </WrapperSearchAndButton>
      </RecordsContainer>
      <CustomTableContainer>
        <Box className="table-container" ref={scrollContainerRef}>
          <Table aria-label="register user table">
            <CustomTableHead>
              <TableHeader
                isSortable={isSortable}
                header={headerRegisterUser}
                paramsGetList={paramsGetList}
                handleRequestSort={handleRequestSort}
              />
            </CustomTableHead>
            <TableBody>
              {dataList && dataList?.length > 0
                ? dataList?.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell align="center">{getIndex(index)}</TableCell>
                      <TableCell align="left">
                        <Box display="flex" alignItems="center" justifyContent="start" gap="7px">
                          {row?.services?.map(
                            (icon: any, idx) =>
                              isIconVisible(icon?.status) && (
                                <img
                                  key={idx}
                                  src={getIconUrlWithStatus(icon)}
                                  alt="icon"
                                  style={{ width: '33px', height: 'auto' }}
                                />
                              )
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="left">
                        <IconButton
                          onClick={() => navigateEditSystem(row.id)}
                          disabled={isDisabledEditSystem(row?.services)}
                          sx={{ opacity: isDisabledEditSystem(row?.services) ? 0.4 : 1 }}
                        >
                          <img
                            src={setting_icon}
                            alt="settings"
                            style={{ width: '25px', height: 'auto', cursor: 'pointer' }}
                          />
                        </IconButton>
                      </TableCell>
                      <TableCell align="left">{row?.company_name || ''}</TableCell>
                      <TableCell align="left">{row?.department || ''}</TableCell>
                      <TableCell align="left">{row?.username || ''}</TableCell>
                      <TableCell align="left">{row?.email || ''}</TableCell>
                      <TableCell align="left">{row?.address || ''}</TableCell>
                      <TableCell align="left">{row?.company_phone_number || ''}</TableCell>
                      <TableCell align="left">{row?.phone_number || ''}</TableCell>
                      <TableCell align="left">{row?.created_at || ''}</TableCell>
                      <TableCell align="center">
                        <IconButton onClick={() => handleRemove(row?.id)}>
                          <img
                            src={remove_icon}
                            alt="remove"
                            style={{ width: '25px', height: 'auto', cursor: 'pointer' }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                : null}
            </TableBody>
          </Table>
          {dataList?.length === 0 && !isLoading && (
            <BoxNoDataList>{NOTIFICATION_MESSAGES.ERROR.SEARCH}</BoxNoDataList>
          )}
        </Box>
      </CustomTableContainer>

      <PaginationComponent
        totalPages={totalPages}
        currentPage={currentPage}
        dataLength={totalRecords}
        handleChangePage={handleChangePage}
      />
    </PageContainer>
  );
};

export default UserInfo;
